import type { ToolId } from 'config/toolDefinitionsConfig';
import type { volumeColorType } from 'config/colorUtils';
import { useEffect, useState } from 'react';
import styles from '../SegmentManagerList.module.scss';
import Button from '@components/Button';
import Switch from '@components/Switch';
import Slider from '@components/Slider';
import {
  getMouseTooltipTriggerProps,
  type MouseTooltipHandlers,
} from '@components/Tooltip/useMouseTooltip';
import { AppConfig } from 'config/config';

type RenderModes = {
  heartCut: boolean;
  ventricle: boolean;
  coronaryAorta: boolean;
  MIP: boolean;
  colorType: volumeColorType;
};

type Props = {
  modes: RenderModes;
  hasVR: boolean;
  segmentationReady: boolean;
  coronaryReady: boolean;
  invisibleToolIds: ToolId[];
  mipToggleActive?: boolean;
  vrType: volumeColorType;
  tooltipHandlers: MouseTooltipHandlers;
  isSegmentationShape: boolean;
  config?: AppConfig;
  onToggleHeartCut: () => void;
  handlelightingChange: (params: { value: number; enabled: boolean }) => void;
  onToggleSegmentationGroup: (
    mode: 'ventricle' | 'coronaryAorta' | 'la',
  ) => void;
  onToggleMIP: () => void;
  onToggleVolumeColorType: (mode: volumeColorType) => void;
};

const COLOR_PRESETS = ['warm', 'red', 'orangeRed'] as const;
const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

export default function SegmentationShapeControlPanel({
  modes,
  hasVR,
  segmentationReady,
  coronaryReady,
  invisibleToolIds,
  mipToggleActive,
  vrType,
  tooltipHandlers,
  config,
  isSegmentationShape,
  handlelightingChange,
  onToggleHeartCut,
  onToggleSegmentationGroup,
  onToggleMIP,
  onToggleVolumeColorType,
}: Props) {
  const tooltipProps = (label: string) =>
    getMouseTooltipTriggerProps(label, tooltipHandlers);
  const lightingOption = hasVR
    ? config?.lightingOptions.VR
    : config?.lightingOptions.Surface3D;
  const [ambientInputValue, setAmbientInputValue] = useState('');

  const isMIPMode = mipToggleActive && hasVR;

  useEffect(() => {
    if (!lightingOption) return;
    setAmbientInputValue(String(lightingOption.ambient));
  }, [lightingOption?.ambient]);

  const handleLightValueChange = (value: number) => {
    if (!lightingOption || Number.isNaN(value)) return;

    handlelightingChange({
      value: clamp01(Number(value)),
      enabled: lightingOption.enabled,
    });
  };

  const handleLightEnabledChange = (enabled: boolean) => {
    if (!lightingOption) return;

    handlelightingChange({
      value: lightingOption.ambient,
      enabled,
    });
  };

  const renderSegmentButton = (
    label: string,
    mode: 'ventricle' | 'coronaryAorta' | 'la',
  ) => (
    <Button
      className="segment-manager-button"
      {...tooltipProps(label)}
      disabled={isMIPMode || !segmentationReady}
      onClick={() => onToggleSegmentationGroup(mode)}
    >
      {label}
    </Button>
  );

  const renderLightControl = () => {
    if (!lightingOption || (isMIPMode && isSegmentationShape)) return null;
    return (
      <div className={styles.lightControlRow}>

        <span className={styles.lightControlLabel}>Light</span>

        <div className={styles.ambientLightSizeRow}>
          <div className={styles.ambientLightSliderContainer}>
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={lightingOption.ambient}
              disabled={!lightingOption.enabled}
              onChange={handleLightValueChange}
            />
          </div>

          <input
            className={styles.lightControlInput}
            type="number"
            value={ambientInputValue}
            min={0}
            max={1}
            step={0.01}
            disabled={!lightingOption.enabled}
            onChange={(event) => {
              const { value } = event.target;
              setAmbientInputValue(value);
              if (value === '' || value === '.') return;
              handleLightValueChange(Number(value));
            }}
            onBlur={() => {
              if (ambientInputValue === '') {
                setAmbientInputValue(String(lightingOption.ambient));
                return;
              }

              const value = clamp01(Number(ambientInputValue));
              setAmbientInputValue(String(value));
              handleLightValueChange(value);
            }}
            onKeyDown={(event) => {
              const value = Number(event.currentTarget.value);

              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }

              if (
                (event.key === 'ArrowUp' && value >= 1) ||
                (event.key === 'ArrowDown' && value <= 0)
              ) {
                event.preventDefault();
              }

              event.stopPropagation();
            }}
          />
        </div>

        <Switch
          id="light-switch"
          checked={lightingOption.enabled}
          onChange={handleLightEnabledChange}
        />
      </div>
    );
  };

  const renderHeartCutControl = () => (
    <div className={styles.toolFrame}>
      <span className={styles.toolName}>Tool</span>
      <div className={styles.toolItemContainer}>
        <div className={styles.toolItem}>
          <span className={styles.toolItemLabel}>HeartCut</span>

          <Switch
            id="heart-cut-switch"
            checked={modes.heartCut}
            onChange={onToggleHeartCut}
            aria-pressed={modes.heartCut && !modes.MIP}
            disabled={
              isMIPMode || !segmentationReady
            }
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className={styles.divider} />

      <div className={styles.toolContainer}>
        Segmentation Shape Control
        <div className={styles.figmaToolBar}>
          <div className={styles.toolBar}>
            {renderLightControl()}

            {isSegmentationShape && renderHeartCutControl()}

            {isSegmentationShape && (
              <div className={styles.toolFrame}>
                <div className={styles.toolItemContainer2}>
                  {renderSegmentButton('Ventricle', 'ventricle')}
                  {renderSegmentButton('Coronary + Aorta', 'coronaryAorta')}
                  {renderSegmentButton('LA', 'la')}
                </div>

                <div className={styles.toolItemContainer2}>
                  <Button
                    className={`segment-manager-button${isMIPMode ? ' active' : ''}`}
                    {...tooltipProps('MIP')}
                    aria-pressed={isMIPMode}
                    disabled={!hasVR}
                    onClick={onToggleMIP}
                  >
                    MIP
                  </Button>

                  {COLOR_PRESETS.map((type, idx) => {
                    const id = `ColorPreset_Type${idx + 1}` as ToolId;
                    const isActive =
                      hasVR && !mipToggleActive && vrType === type;

                    return (
                      <Button
                        key={type}
                        className={`segment-manager-button${isActive ? ' active' : ''}`}
                        {...tooltipProps(`Type${idx + 1}`)}
                        aria-pressed={
                          modes.colorType === type && !modes.MIP && hasVR
                        }
                        disabled={invisibleToolIds.includes(id)}
                        onClick={() => onToggleVolumeColorType(type)}
                      >
                        {`Type${idx + 1}`}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
